/**
 * canvasGeometry.ts — pure geometry helpers for the workflow canvas.
 *
 * No React, no DOM mutation — just math over plain rectangles so the edge
 * layer and drag logic can be unit-reasoned-about independently of when/how
 * React re-renders. Callers read live `offsetLeft/Top/Width/Height` from
 * node DOM elements and hand the resulting rect in here.
 */

import type { OutputPort } from "../workflow.types";

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

/** Snap distance (px) for "is the cursor over this input port". */
export const PORT_SNAP_DISTANCE = 14;

/** Node card width in px — kept in sync with the `.wf-node` CSS rule. */
export const NODE_WIDTH = 272;

/** Clamp range for the user drag-resizing a node card via its corner handle —
 * wide enough for a long label or several condition branches, never so small
 * the header/menu button stop fitting, never so large one node swallows the
 * canvas. Height has no CSS default (content drives it), so its minimum is
 * just "shorter than that looks broken", not a mirrored default. */
export const MIN_NODE_WIDTH = 220;
export const MAX_NODE_WIDTH = 560;
export const MIN_NODE_HEIGHT = 72;
export const MAX_NODE_HEIGHT = 640;

/** One of the 8 drag handles around a node card — the 4 edges plus the 4
 * corners, matching every other resizable-box convention (image editors,
 * window managers, etc). */
export type ResizeDirection = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

export interface ResizeRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Pure resize math: given the node's rect *before* the drag started and how
 * far the pointer has moved, returns the new rect for this direction.
 *
 * Dragging an edge/corner that includes "w" or "n" grows the node toward the
 * opposite side — the node's x/y must shift so the edge under the cursor
 * moves while the far edge stays put (exactly like resizing any OS window).
 * The shift is derived from the *actual* (clamped) size change, not the raw
 * pointer delta, so hitting MIN/MAX_NODE_WIDTH/HEIGHT stops the edge dead
 * rather than letting the node drift away from the cursor.
 */
export function applyResize(direction: ResizeDirection, start: ResizeRect, dx: number, dy: number): ResizeRect {
  let { x, y, width, height } = start;

  if (direction.includes("e")) {
    width = clamp(start.width + dx, MIN_NODE_WIDTH, MAX_NODE_WIDTH);
  } else if (direction.includes("w")) {
    width = clamp(start.width - dx, MIN_NODE_WIDTH, MAX_NODE_WIDTH);
    x = Math.max(0, start.x + (start.width - width));
  }

  if (direction.includes("s")) {
    height = clamp(start.height + dy, MIN_NODE_HEIGHT, MAX_NODE_HEIGHT);
  } else if (direction.includes("n")) {
    height = clamp(start.height - dy, MIN_NODE_HEIGHT, MAX_NODE_HEIGHT);
    y = Math.max(0, start.y + (start.height - height));
  }

  return { x, y, width, height };
}

/** Minimum canvas surface size, and the row-wrap threshold used by `nextFreePosition`. */
export const CANVAS_WIDTH = 3200;
export const CANVAS_HEIGHT = 2000;

/** Room left around the furthest node so it never sits flush against the scroll edge. */
const CANVAS_PADDING = 320;
/** No live height measurement is available at sizing time — a generous fixed
 * estimate (taller than any real node, including a condition node) is safe
 * because this only pads scrollable area, never affects port/edge math. */
const NODE_HEIGHT_ESTIMATE = 220;

/**
 * The canvas surface should hug the nodes actually on it — not force a fixed
 * 3200x2000 scroll area when a workflow has three nodes in a corner. Grows to
 * fit content plus padding; never shrinks below the viewport itself (so there's
 * always somewhere to drop a new node) or below the minimum default size.
 */
export function computeCanvasSize(
  nodes: { x: number; y: number; config?: { width?: number; height?: number } }[],
  viewportWidth: number,
  viewportHeight: number,
): { width: number; height: number } {
  const minWidth = Math.max(viewportWidth || 0, 900);
  const minHeight = Math.max(viewportHeight || 0, 600);
  if (!nodes.length) return { width: minWidth, height: minHeight };
  const maxX = Math.max(...nodes.map((n) => n.x + (n.config?.width || NODE_WIDTH))) + CANVAS_PADDING;
  const maxY = Math.max(...nodes.map((n) => n.y + (n.config?.height || NODE_HEIGHT_ESTIMATE))) + CANVAS_PADDING;
  return { width: Math.min(Math.max(minWidth, maxX), CANVAS_WIDTH), height: Math.min(Math.max(minHeight, maxY), CANVAS_HEIGHT) };
}

/** Which of a condition node's N ports a point is being asked for — evenly
 * spaced down the node's right edge regardless of how many there are. */
export interface ConditionPortMeta {
  index: number;
  count: number;
}

/**
 * Position of a named port on a node, relative to the canvas's own
 * coordinate space (both rects must already be relative to that space).
 * `conditionMeta` positions a condition node's Nth-of-M output port —
 * required for `port` values other than "in"/"out" (i.e. every condition
 * option, including the legacy "yes"/"no" pair, which is just N=2).
 */
export function getPortPoint(rect: Rect, port: OutputPort | "in", conditionMeta?: ConditionPortMeta): Point {
  const { left, top, width, height } = rect;
  if (port === "in") return { x: left, y: top + height / 2 };
  if (port === "out") return { x: left + width, y: top + height / 2 };
  if (conditionMeta) {
    const fraction = (conditionMeta.index + 1) / (conditionMeta.count + 1);
    return { x: left + width, y: top + height * fraction };
  }
  return { x: left + width, y: top + height / 2 };
}

/** CSS class suffix (`wf-port--<suffix>`, `wf-edge--<suffix>`, etc.) for a
 * condition option at this index — the first two keep the classic "yes"/"no"
 * names for exact visual continuity with every workflow saved before this
 * feature; index 2 onward cycles through the extra palette colors. */
const CONDITION_COLOR_SUFFIXES = ["yes", "no", "opt-2", "opt-3", "opt-4", "opt-5"];
export function conditionColorSuffix(index: number): string {
  return CONDITION_COLOR_SUFFIXES[index % CONDITION_COLOR_SUFFIXES.length];
}

/** Cubic-bezier "d" attribute for a smooth horizontal connector between two points. */
export function bezierPath(from: Point, to: Point): string {
  const dx = Math.max(Math.abs(to.x - from.x) * 0.5, 40);
  const c1x = from.x + dx;
  const c2x = to.x - dx;
  return `M ${from.x} ${from.y} C ${c1x} ${from.y}, ${c2x} ${to.y}, ${to.x} ${to.y}`;
}

/** Midpoint of the same cubic curve (approx, at t=0.5) — used for edge label pills. */
export function bezierMidpoint(from: Point, to: Point): Point {
  const dx = Math.max(Math.abs(to.x - from.x) * 0.5, 40);
  const c1x = from.x + dx;
  const c2x = to.x - dx;
  const t = 0.5;
  const x =
    (1 - t) ** 3 * from.x +
    3 * (1 - t) ** 2 * t * c1x +
    3 * (1 - t) * t ** 2 * c2x +
    t ** 3 * to.x;
  const y =
    (1 - t) ** 3 * from.y +
    3 * (1 - t) ** 2 * t * from.y +
    3 * (1 - t) * t ** 2 * to.y +
    t ** 3 * to.y;
  return { x, y };
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** A sensible free spot for a newly added node, offset from the last one. */
export function nextFreePosition(lastX?: number, lastY?: number): Point {
  if (lastX === undefined || lastY === undefined) return { x: 80, y: 160 };
  const x = lastX + 300;
  if (x + NODE_WIDTH > CANVAS_WIDTH) return { x: 80, y: lastY + 200 };
  return { x, y: lastY };
}
